import { Link } from "react-router-dom";
import { Mic2, Settings2, User, VolumeX } from "lucide-react";
import ProfileCard from "@/components/creator/music/ProfileCard";

const PLAIN = [
  { mode: "male", label: "Male lead", hint: "A male lead vocal", Icon: User },
  { mode: "female", label: "Female lead", hint: "A female lead vocal", Icon: Mic2 },
  { mode: "instrumental", label: "Instrumental", hint: "No vocals — music only", Icon: VolumeX },
];

function MyVoicePicker({ voices, voiceId, onPick }) {
  if (!voices) return <p className="mt-3 text-[12px] text-white/45">Loading your recorded voices…</p>;
  if (!voices.length) {
    return <p data-testid="vocal-no-voices" className="mt-3 text-[12px] text-amber-200/80">No recorded voice yet — create one in the Advanced Studio (Voice Clone) first.</p>;
  }
  return (
    <label className="mt-3 block">
      <span className="mb-1 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/55">Recorded voice</span>
      <select data-testid="vocal-my-voice-select" value={voiceId || ""} onChange={(e) => onPick(e.target.value || null)} className="w-full rounded-xl border border-white/10 bg-black/50 px-3 py-2 text-sm text-white focus:border-amber-300/60 focus:outline-none">
        <option value="">Choose your voice…</option>
        {voices.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}</option>)}
      </select>
    </label>
  );
}

/** Integrated vocal selection — your signature voice profiles first (main on top), then plain voices. */
export default function VocalPanel({ catalog, vocal, vocalStyle, voices, profiles, arrangementType, onChange }) {
  const setVocal = (patch) => onChange({ vocal: { ...vocal, ...patch } });
  const list = profiles?.length ? profiles : [{ id: null, is_main: true, name: catalog.signature_voice.name, description: catalog.signature_voice.description }];
  const isSelected = (p) => vocal.mode === "signature" && (vocal.profile_id ? vocal.profile_id === p.id : p.is_main);
  const pick = (p) => setVocal({ mode: "signature", profile_id: p.id, voice_id: p.cloned_voice_id || vocal.voice_id || null });
  const groupSong = arrangementType === "duet" || arrangementType === "choir";
  return (
    <section data-testid="vocal-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-white/70">Lead vocal</p>
        <Link to="/create/voices" data-testid="vocal-manage-profiles" className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/50 hover:text-white">
          <Settings2 className="h-3.5 w-3.5" /> Voice profiles
        </Link>
      </div>
      <div className="space-y-2">
        {list.map((p) => (
          <ProfileCard key={p.id || "signature"} profile={p} selected={isSelected(p)} source={vocal.source} onSelect={pick} onSource={(source) => setVocal({ source })}>
            {vocal.source === "my_voice" ? (
              <>
                <MyVoicePicker voices={voices} voiceId={vocal.voice_id} onPick={(id) => setVocal({ voice_id: id })} />
                <p data-testid="vocal-my-voice-note" className={`mt-2 text-[11px] leading-relaxed ${groupSong ? "text-amber-200/85" : "text-white/45"}`}>
                  {groupSong
                    ? "My Recorded Voice re-sings the whole vocal, so it can't be used for a duet or choir song — pick AI Gospel Voice, then use “Sing it myself” on the finished song."
                    : "ELPM first sings the song in this voice's style, then isolates the lead vocal and converts it to your voice. Experimental — high notes and long runs can wobble."}
                </p>
              </>
            ) : null}
          </ProfileCard>
        ))}
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2">
        {PLAIN.map(({ mode, label, hint, Icon }) => (
          <button key={mode} type="button" data-testid={`voice-${mode}`} aria-pressed={vocal.mode === mode} onClick={() => setVocal({ mode })}
            className={`rounded-2xl border px-3 py-3 text-left transition-colors duration-200 ${vocal.mode === mode ? "border-violet-400/60 bg-violet-500/20 text-white" : "border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/[0.07]"}`}>
            <Icon className="mb-1.5 h-4 w-4" />
            <p className="font-display text-[13px] font-semibold">{label}</p>
            <p className="mt-0.5 hidden text-[11px] text-white/50 sm:block">{hint}</p>
          </button>
        ))}
      </div>
      {vocal.mode !== "instrumental" ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="mr-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white/50">Delivery</span>
          {catalog.vocal_styles.map((v) => (
            <button key={v} type="button" data-testid={`vocal-style-${v.replace(/\W+/g, "-").toLowerCase()}`} aria-pressed={vocalStyle === v} onClick={() => onChange({ vocal_style: vocalStyle === v ? null : v })}
              className={`rounded-full border px-3 py-1 text-[12px] transition-colors ${vocalStyle === v ? "border-blue-400/60 bg-blue-500/20 text-white" : "border-white/10 bg-white/[0.03] text-white/65 hover:bg-white/[0.08]"}`}>
              {v}
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}
